/**
 * Enum of error strings returned by the server, plus some generic error messages
 */
export enum ImportMeetingError {
  // Zoom
  PMI = "PMI",
  RECURRING = "RECURRING",
  INSTANT = "INSTANT",
  ZOOM_NATIVE_DISABLED = "Zoom disabled",
  ZOOM_DISABLED = "Zoom is disabled",
  HOST_CLOUD_RECORDING_DISABLED = "Host cloud recording disabled",

  // Google Meet
  GOOGLE_MEET_DISABLED = "Google Meet is disabled",

  // Teams
  TEAMS_DISABLED = "Microsoft Teams is disabled",
  RECORDER_IN_WAITING_ROOM = "Recorder in waiting room",

  // General
  MEETING_NOT_FOUND = "Meeting not found",
  MEETING_NOT_FOUND_NATIVE = "Meeting not found - Native",
  INVALID_INTERVIEWER = "Invalid interviewer",
  CANDIDATE_OPT_OUT = "Candidate opt-out",
  USER_IS_INTERNAL_CANDIDATE = "User is internal candidate",
  GENERIC_ERROR = "_",
}

const errorMap: Record<ImportMeetingError | string, MessageContent> = {
  // Zoom
  [ImportMeetingError.PMI]: {
    primary: "BrightHire doesn’t record meetings using a ‘Personal Meeting ID’",
    secondary:
      "BrightHire Notes and Recordings are not available for this meeting type.",
  },
  [ImportMeetingError.RECURRING]: {
    primary: "BrightHire doesn’t record recurring meetings such as this one.",
    secondary: "BrightHire is not available for this meeting type.",
  },
  [ImportMeetingError.INSTANT]: {
    primary: "BrightHire doesn’t record instant meetings such as this one.",
    secondary: "BrightHire is not available for this meeting type.",
  },
  [ImportMeetingError.ZOOM_NATIVE_DISABLED]: {
    primary: "Zoom is not setup for your organization yet.",
    secondary:
      "BrightHire is only available on Zoom meetings – please ask your BrightHire admin.",
  },
  [ImportMeetingError.ZOOM_DISABLED]: {
    primary: "Zoom is not setup for your organization.",
    secondary: "Please ask your BrightHire admin to enable Zoom.",
  },
  [ImportMeetingError.HOST_CLOUD_RECORDING_DISABLED]: {
    primary: "Please ask the meeting host to enable cloud recording",
    secondary:
      "The meeting host currently has cloud recording disabled in their user settings. This must be resolved in order to record the meeting. User settings can take up to an hour to propagate after they are changed in Zoom.",
  },

  // Google Meet
  [ImportMeetingError.GOOGLE_MEET_DISABLED]: {
    primary: "Google Meet is not setup for your organization.",
    secondary: "Please ask your BrightHire admin to enable Google Meet.",
  },

  // Teams
  [ImportMeetingError.TEAMS_DISABLED]: {
    primary: "Microsoft Teams is not setup for your organization.",
    secondary: "Please ask your BrightHire admin to enable Microsoft Teams.",
  },
  [ImportMeetingError.RECORDER_IN_WAITING_ROOM]: {
    primary: "Please admit the BrightHire Notetaker to this meeting.",
    secondary:
      "Once BrightHire Notetaker is let in, we'll record the meeting and load your BrightHire experience. ",
  },

  // General
  [ImportMeetingError.MEETING_NOT_FOUND]: {
    primary: "There isn't a meeting we can join.",
    secondary:
      "We either couldn't find a meeting, or the meeting is owned by another organization.",
  },
  [ImportMeetingError.MEETING_NOT_FOUND_NATIVE]: {
    primary: "We couldn't find a meeting to join.",
    secondary:
      "We either weren't scheduled to join the meeting or couldn't find the meeting.",
  },
  [ImportMeetingError.INVALID_INTERVIEWER]: {
    primary: "There isn't a meeting we can join.",
    secondary:
      "We either couldn't find a meeting, or the meeting is owned by another organization.",
  },
  [ImportMeetingError.CANDIDATE_OPT_OUT]: {
    primary: "This candidate opted out of BrightHire recordings.",
    secondary:
      "This candidate requested not to be recorded, so BrightHire is not available for this user.",
  },
  [ImportMeetingError.USER_IS_INTERNAL_CANDIDATE]: {
    primary:
      "BrightHire is available only to the person conducting the interview.",
    secondary: "You are listed as the candidate for this interview. Good luck!",
  },
  [ImportMeetingError.GENERIC_ERROR]: {
    primary: "An unexpected error has occurred.",
    secondary: "Please reload the page or try again in a few minutes.",
  },
};

/**
 * Returns appropriate long-form error messaging for a given short-form key
 */
export const getErrorContent = (key: string): MessageContent => {
  const trimmedKey = key.replace("Invalid meeting type: ", "").toLowerCase();
  const validatedKey = Object.keys(errorMap).find(
    (k) => k.toLowerCase() === trimmedKey
  );
  const value = validatedKey ? errorMap[validatedKey] : undefined;
  return value ?? errorMap[ImportMeetingError.GENERIC_ERROR];
};

interface MessageContent {
  primary: string;
  secondary: string;
}
